import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Close as CloseIcon,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { WatchlistEntry } from '../types';
import { StockService } from '../services/stockService';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

interface StockInfoDialogProps {
  open: boolean;
  onClose: () => void;
  symbol: string | null;
}

interface InfoRowProps {
  label: string;
  value: string | number | undefined | null;
  suffix?: string;
  prefix?: string;
  highlight?: 'positive' | 'negative' | 'neutral';
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, suffix = '', prefix = '', highlight }) => {
  if (value === undefined || value === null || value === '') return null;

  const getColor = () => {
    if (highlight === 'positive') return 'success.main';
    if (highlight === 'negative') return 'error.main';
    return 'text.primary';
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight="medium" color={getColor()}>
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </Typography>
    </Box>
  );
};

const StockInfoDialog: React.FC<StockInfoDialogProps> = ({
  open,
  onClose,
  symbol,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stock, setStock] = useState<WatchlistEntry | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && symbol) {
      loadStockData();
    }
  }, [open, symbol]);

  const loadStockData = async () => {
    if (!symbol) return;

    setLoading(true);
    setError(null);

    try {
      const watchlist = await StockService.getWatchlist(true); // Include inactive
      const entry = watchlist.find(w => w.symbol === symbol);

      if (entry) {
        setStock(entry);
      } else {
        setError(`Stock ${symbol} not found in watchlist`);
      }
    } catch (err) {
      setError('Failed to load stock data');
      console.error('Error loading stock data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined) return '-';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatLargeNumber = (value: number | undefined) => {
    if (value === undefined) return '-';
    if (value >= 1000000000) return `${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(2)}K`;
    return value.toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">
              {stock?.symbol || symbol}
            </Typography>
            {stock?.companyName && (
              <Typography variant="body2" color="text.secondary">
                - {stock.companyName}
              </Typography>
            )}
            {stock?.isActive === false && (
              <Chip label="Inactive" size="small" color="default" />
            )}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box display="flex" justifyContent="center" p={4}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && stock && (
          <>
            {/* Price Header */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Current Price
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(stock.currentPrice)}
                  </Typography>
                  {stock.priceChgPercent !== undefined && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {stock.priceChgPercent >= 0 ? (
                        <TrendingUp color="success" fontSize="small" />
                      ) : (
                        <TrendingDown color="error" fontSize="small" />
                      )}
                      <Typography
                        variant="body2"
                        color={stock.priceChgPercent >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatPercent(stock.priceChgPercent)}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Pivot Price
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(stock.pivotPrice)}
                  </Typography>
                  {stock.distanceFromPivot !== null && stock.distanceFromPivot !== undefined && (
                    <Typography
                      variant="body2"
                      color={stock.distanceFromPivot <= 0 ? 'success.main' : 'warning.main'}
                    >
                      {formatPercent(stock.distanceFromPivot)} from pivot
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Target Price
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(stock.targetPrice)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              variant={fullScreen ? 'scrollable' : 'standard'}
              scrollButtons={fullScreen ? 'auto' : false}
            >
              <Tab label="Overview" />
              <Tab label="Technical" />
              <Tab label="Ratings" />
              <Tab label="Notes" />
            </Tabs>

            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Price Information
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <InfoRow label="Current Price" value={formatCurrency(stock.currentPrice)} />
                  <InfoRow label="Pivot Price" value={formatCurrency(stock.pivotPrice)} />
                  <InfoRow label="Target Price" value={formatCurrency(stock.targetPrice)} />
                  <InfoRow
                    label="Distance from Pivot"
                    value={stock.distanceFromPivot !== null ? formatPercent(stock.distanceFromPivot) : '-'}
                    highlight={stock.distanceFromPivot !== null && stock.distanceFromPivot <= 0 ? 'positive' : 'neutral'}
                  />
                  <InfoRow label="% Off High" value={stock.percentOffHigh !== undefined ? `${stock.percentOffHigh.toFixed(1)}%` : '-'} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Volume Information
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <InfoRow label="Current Volume" value={formatLargeNumber(stock.volume)} />
                  <InfoRow label="Avg Daily Volume" value={formatLargeNumber(stock.avgDailyVolume)} />
                  <InfoRow
                    label="Vol vs 50-Day Avg"
                    value={stock.volumeChgVs50Day !== undefined ? formatPercent(stock.volumeChgVs50Day) : '-'}
                    highlight={stock.volumeChgVs50Day !== undefined && stock.volumeChgVs50Day > 0 ? 'positive' : 'neutral'}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Company Information
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <InfoRow label="Company Name" value={stock.companyName} />
                  <InfoRow label="Industry" value={stock.industryName} />
                  <InfoRow label="Industry Rank" value={stock.indGroupRank} suffix=" (lower is better)" />
                  <InfoRow label="Market Cap" value={stock.marketCap ? `$${formatLargeNumber(stock.marketCap * 1000000)}` : '-'} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Valuation
                  </Typography>
                  <Divider sx={{ mb: 1}} />
                  <InfoRow label="P/E Ratio" value={stock.peRatio?.toFixed(1)} />
                  <InfoRow label="Forward P/E" value={stock.forwardPeRatio?.toFixed(1)} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tracking Information
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <InfoRow
                    label="Added to Watchlist"
                    value={stock.addedAt ? format(stock.addedAt, 'MMM d, yyyy') : '-'}
                  />
                  <InfoRow
                    label="Last Updated"
                    value={stock.lastUpdated ? format(stock.lastUpdated, 'MMM d, yyyy h:mm a') : '-'}
                  />
                  <InfoRow label="Data Provider" value={stock.dataProvider} />
                  {stock.dataDelay !== undefined && stock.dataDelay > 0 && (
                    <InfoRow label="Data Delay" value={`${stock.dataDelay} minutes`} />
                  )}
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Base Pattern
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <InfoRow label="Base Type" value={stock.weeklyBaseType} />
                  <InfoRow label="Base Stage" value={stock.weeklyBaseStage ? `Stage ${stock.weeklyBaseStage}` : '-'} />
                  <InfoRow
                    label="Weekly Change to Pivot"
                    value={stock.weeklyChgToPivot !== undefined ? formatPercent(stock.weeklyChgToPivot) : '-'}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Price Action
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <InfoRow
                    label="Price Change %"
                    value={stock.priceChgPercent !== undefined ? formatPercent(stock.priceChgPercent) : '-'}
                    highlight={stock.priceChgPercent !== undefined && stock.priceChgPercent > 0 ? 'positive' : stock.priceChgPercent !== undefined && stock.priceChgPercent < 0 ? 'negative' : 'neutral'}
                  />
                  <InfoRow label="% Off High" value={stock.percentOffHigh !== undefined ? `${stock.percentOffHigh.toFixed(1)}%` : '-'} />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    IBD Ratings
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <InfoRow label="Composite Rating" value={stock.compRating} suffix="/99" />
                  <InfoRow label="EPS Rating" value={stock.epsRating} suffix="/99" />
                  <InfoRow label="RS Rating" value={stock.rsRating} suffix="/99" />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Additional Ratings
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <InfoRow label="A/D Rating" value={stock.adRating} />
                  <InfoRow label="SMR Rating" value={stock.smrRating} />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <Box sx={{ minHeight: 150 }}>
                {stock.notes ? (
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {stock.notes}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    No notes for this stock. Add notes from the Watchlist screen using the Update Stock dialog.
                  </Typography>
                )}
              </Box>
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockInfoDialog;
